import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LogOut, TrendingUp, Shield, RefreshCw, Gamepad2, FileText, Library, Clock, ChevronRight, Flame, Droplet, Zap, Leaf, Activity, Heart, Stethoscope, ShoppingBag, Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL with cache busting
const getFullLogoUrl = (url) => {
  if (!url) return null;
  let fullUrl = url;
  if (url.startsWith('/uploads')) {
    fullUrl = `${BACKEND_URL}/api${url}`;
  } else if (url.startsWith('/')) {
    fullUrl = `${BACKEND_URL}${url}`;
  } else if (!url.startsWith('http')) {
    fullUrl = `${BACKEND_URL}/${url}`;
  }
  // Add cache busting
  const separator = fullUrl.includes('?') ? '&' : '?';
  return `${fullUrl}${separator}v=${Date.now()}`;
};

// Default MedStudyPro Logo Component - Updated geometric M design
const DefaultLogo = ({ dark = false }) => (
  <div className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left M leg */}
      <path d="M4 32V10L12 18L20 10" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Right M leg */}
      <path d="M20 10L28 18L36 10V32" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Center cube - top face */}
      <path d="M20 16L24 18V22L20 24L16 22V18L20 16Z" fill="#334155"/>
      {/* Center cube - visible faces */}
      <path d="M20 16L24 18L20 20L16 18L20 16Z" fill="#475569"/>
      <path d="M20 20L24 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.6"/>
      <path d="M20 20L16 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.4"/>
    </svg>
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Brand Logo with uploaded image + text
const BrandLogo = ({ logoUrl, dark = false }) => (
  <div className="flex items-center gap-2">
    <img 
      src={logoUrl} 
      alt="MedStudyPro" 
      className="h-8 w-8 object-contain"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [topics, setTopics] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [progressSummary, setProgressSummary] = useState(null);
  const [caseStudies, setCaseStudies] = useState([]);
  const [completedCases, setCompletedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoSettings, setLogoSettings] = useState({ logo_url: null, logo_light_url: null });
  const [subscription, setSubscription] = useState(null);
  const [featureAccess, setFeatureAccess] = useState({
    games: true,
    flashcards: true,
    case_studies: true
  });
  const navigate = useNavigate();
  const location = useLocation();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [userRes, topicsRes, perfRes, progressRes, casesRes, logoRes, subStatusRes, caseAttemptsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/topics`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/performance/user`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/progress/summary`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/case-studies`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/logo-settings`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/subscription/status`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/user/case-attempts?status=completed`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setUser(userRes.data);
      setTopics(topicsRes.data || []);
      setPerformance(perfRes.data || []);
      setProgressSummary(progressRes.data);
      setCaseStudies(casesRes.data || []);
      setCompletedCases(caseAttemptsRes.data || []);
      setLogoSettings(logoRes.data || {});
      
      // Set subscription status
      const subData = subStatusRes.data || {};
      setSubscription(subData);
      
      // Check feature access based on subscription
      // Admins, Pro users, and Beta users have full access
      const isAdmin = subData.isAdmin || userRes.data?.role === 'admin';
      const isPro = subData.currentPlan?.toLowerCase() === 'pro';
      const isBeta = subData.currentPlan?.toLowerCase() === 'beta';
      const hasProAccess = isAdmin || isPro || isBeta;
      
      setFeatureAccess({
        games: hasProAccess,
        flashcards: hasProAccess,
        case_studies: hasProAccess || (subData.currentPlan?.toLowerCase() === 'basic')
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/');
        return;
      }
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData, location.key]);

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      // Also clear backend session
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const getTopicPerformance = (topicKey) => {
    return performance.find(p => p.topic_id === topicKey || p.topic_key === topicKey);
  };

  const getStatusColor = (accuracy) => {
    if (accuracy >= 80) return { bg: 'bg-green-500', text: 'text-green-600', label: 'Good' };
    if (accuracy >= 60) return { bg: 'bg-[#FF9E00]', text: 'text-[#FF9E00]', label: 'Needs Review' };
    return { bg: 'bg-red-500', text: 'text-red-600', label: 'Weak' };
  };

  const getTopicIcon = (index) => {
    const icons = [
      <Droplet key="droplet" className="w-5 h-5 text-[#FF9E00]" />,
      <Zap key="zap" className="w-5 h-5 text-yellow-500" />,
      <Leaf key="leaf" className="w-5 h-5 text-green-500" />,
      <Activity key="activity" className="w-5 h-5 text-blue-500" />,
      <Heart key="heart" className="w-5 h-5 text-red-500" />,
      <Stethoscope key="stethoscope" className="w-5 h-5 text-purple-500" />
    ];
    return icons[index % icons.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#E5F3FF] to-[#CDE4F2]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9E00]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#E5F3FF] to-[#CDE4F2]">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <p className="text-[#1E3A5C] mb-4">{error}</p>
          <Button onClick={fetchData} className="bg-[#FF9E00] hover:bg-[#E88D00] text-white rounded-full px-6">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Get top focus areas based on ACTUAL performance data only
  const focusAreas = performance.length > 0 
    ? performance.slice(0, 3).map((perf, index) => {
        const topic = topics.find(t => t.topic_key === perf.topic_id || t.topic_id === perf.topic_id);
        const topicName = topic?.topic_display_name || topic?.name || perf.topic_id;
        const accuracy = perf.accuracy || 0;
        const status = getStatusColor(accuracy);
        
        return {
          id: perf.topic_id,
          name: topicName,
          accuracy,
          status,
          icon: getTopicIcon(index)
        };
      })
    : []; // Empty array if no performance data

  // Get completed case studies with their scores
  const challenges = completedCases.map((attempt, index) => ({
    id: attempt.case_id,
    attemptId: attempt.attempt_id,
    title: attempt.case_title || 'Unknown Case',
    score: attempt.percent_score ? `${Math.round(attempt.percent_score)}%` : 'N/A',
    performanceTier: attempt.performance_tier || 'Not graded',
    completedAt: attempt.completed_at,
    buttonColor: attempt.percent_score >= 80 ? 'bg-green-500' : attempt.percent_score >= 60 ? 'bg-[#FF9E00]' : 'bg-red-500'
  })).slice(0, 5); // Show up to 5 recent completed cases

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E5F3FF] to-[#CDE4F2]">
      {/* Navigation */}
      <nav className="bg-[#F8FAFD] border-b border-[#D4E3EF]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Use uploaded logo or default - Dashboard has light background, use logo_light_url if available */}
          {logoSettings.logo_light_url || logoSettings.logo_url ? (
            <BrandLogo logoUrl={getFullLogoUrl(logoSettings.logo_light_url || logoSettings.logo_url)} dark={false} />
          ) : (
            <DefaultLogo dark={false} />
          )}
          <div className="flex items-center gap-6">
            <button
              data-testid="nav-products-button"
              onClick={() => navigate('/products')}
              className="flex items-center gap-2 text-[#6D7C8C] hover:text-[#1E3A5C] text-sm font-medium transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              Shop
            </button>
            <button
              data-testid="nav-games-button"
              onClick={() => {
                if (featureAccess.games) {
                  navigate('/games');
                } else {
                  toast.info('Games are available with Pro subscription', {
                    action: {
                      label: 'Upgrade',
                      onClick: () => navigate('/pricing')
                    }
                  });
                }
              }}
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                featureAccess.games 
                  ? 'text-[#6D7C8C] hover:text-[#1E3A5C]' 
                  : 'text-[#6D7C8C]/50 cursor-not-allowed'
              }`}
            >
              <Gamepad2 className="w-4 h-4" />
              Games
              {!featureAccess.games && <Lock className="w-3 h-3 ml-1" />}
            </button>
            <button
              data-testid="nav-progress-button"
              onClick={() => navigate('/progress')}
              className="flex items-center gap-2 text-[#6D7C8C] hover:text-[#1E3A5C] text-sm font-medium transition-colors"
            >
              <TrendingUp className="w-4 h-4" />
              Progress
            </button>
            {user?.role === 'admin' && (
              <button
                data-testid="nav-admin-button"
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-[#6D7C8C] hover:text-[#1E3A5C] text-sm font-medium transition-colors"
              >
                <Shield className="w-4 h-4" />
                Admin
              </button>
            )}
            <button
              data-testid="nav-account-button"
              onClick={() => navigate('/account')}
              className="flex items-center gap-2 text-[#6D7C8C] hover:text-[#1E3A5C] text-sm font-medium transition-colors"
            >
              <User className="w-4 h-4" />
              Account
            </button>
            <button
              data-testid="nav-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#6D7C8C] hover:text-[#1E3A5C] text-sm font-medium transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-[#1E3A5C] mb-2">
            Welcome back—let&apos;s keep you sharp.
          </h1>
          <p className="text-[#4A5E75] text-lg">
            MedStudyPro is ready to challenge you. Pick your next training focus and prove your knowledge.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-10">
          <Button
            data-testid="smart-session-button"
            onClick={() => navigate('/quiz/setup')}
            className="bg-[#FF9E00] hover:bg-[#E88D00] text-white h-12 px-6 rounded-full font-semibold text-sm shadow-lg transition-all"
          >
            Start Smart Session
          </Button>
          <Button
            data-testid="skill-builder-button"
            onClick={() => navigate('/quiz/skill-builder')}
            variant="outline"
            className="bg-white border-[#D4E3EF] hover:border-[#1E3A5C] text-[#1E3A5C] h-12 px-6 rounded-full font-medium text-sm flex items-center gap-2"
          >
            <Flame className="w-4 h-4 text-[#FF9E00]" />
            Skill Builder
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            data-testid="case-challenge-button"
            onClick={() => navigate('/case-studies')}
            variant="outline"
            className="bg-white border-[#D4E3EF] hover:border-[#1E3A5C] text-[#1E3A5C] h-12 px-6 rounded-full font-medium text-sm flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Case Challenge
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="mb-6">
          <button
            onClick={() => navigate('/topics')}
            className="text-[#1E3A5C] text-sm font-medium flex items-center gap-1 hover:text-[#FF9E00] transition-colors"
          >
            Browse Topics <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Your Key Focus Areas */}
          <div>
            <h2 className="text-xl font-bold text-[#1E3A5C] mb-4">Your Key Focus Areas</h2>
            <div className="space-y-4">
              {focusAreas.length > 0 ? focusAreas.map((area) => (
                <div
                  key={area.id}
                  data-testid={`focus-area-${area.id}`}
                  className="bg-white rounded-xl p-5 shadow-sm border border-[#E8EEF3] hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/quiz/setup/${encodeURIComponent(area.id)}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        area.status.label === 'Good' ? 'bg-green-100' : 
                        area.status.label === 'Needs Review' ? 'bg-orange-100' : 'bg-red-100'
                      }`}>
                        {area.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1E3A5C]">{area.name}</h3>
                        <p className={`text-sm ${area.status.text}`}>{area.status.label}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-[#E0E0E0] rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${area.status.bg} rounded-full transition-all`}
                        style={{ width: `${area.accuracy}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-[#1E3A5C] w-12 text-right">{area.accuracy}%</span>
                    <div className="flex gap-0.5">
                      {[1,2,3].map(i => (
                        <div key={i} className={`w-1 ${i <= Math.ceil(area.accuracy/33) ? area.status.bg : 'bg-slate-200'} rounded-full`} style={{height: `${8 + i*4}px`}} />
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8EEF3]">
                    <span className="text-xs text-slate-500">Continue practicing</span>
                    <button className="text-xs text-[#FF9E00] font-medium flex items-center gap-1">
                      Drill Down <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )) : (
                <div className="bg-white rounded-xl p-8 shadow-sm border border-[#E8EEF3] text-center">
                  <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-semibold text-[#1E3A5C] mb-2">Nothing here yet</h3>
                  <p className="text-slate-500 text-sm mb-4">Start your first quiz or game to track your progress.</p>
                  <Button
                    onClick={() => navigate('/quiz/setup')}
                    className="bg-[#FF9E00] hover:bg-[#E88D00] text-white rounded-full px-6"
                  >
                    Start Your First Quiz
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Completed Case Studies */}
          <div>
            <h2 className="text-xl font-bold text-[#1E3A5C] mb-4">Completed Case Studies</h2>
            <div className="space-y-4">
              {challenges.length > 0 ? challenges.map((challenge) => (
                <div
                  key={challenge.attemptId || challenge.id}
                  data-testid={`completed-case-${challenge.id}`}
                  className="bg-white rounded-xl p-5 shadow-sm border border-[#E8EEF3] hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        challenge.score !== 'N/A' && parseInt(challenge.score) >= 80 
                          ? 'bg-green-100' 
                          : challenge.score !== 'N/A' && parseInt(challenge.score) >= 60 
                            ? 'bg-amber-100' 
                            : 'bg-red-100'
                      }`}>
                        <Activity className={`w-5 h-5 ${
                          challenge.score !== 'N/A' && parseInt(challenge.score) >= 80 
                            ? 'text-green-500' 
                            : challenge.score !== 'N/A' && parseInt(challenge.score) >= 60 
                              ? 'text-amber-500' 
                              : 'text-red-500'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1E3A5C] text-sm">{challenge.title}</h3>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-xs text-slate-500">Score: <span className={`font-bold ${
                            challenge.score !== 'N/A' && parseInt(challenge.score) >= 80 
                              ? 'text-green-600' 
                              : challenge.score !== 'N/A' && parseInt(challenge.score) >= 60 
                                ? 'text-amber-600' 
                                : 'text-red-600'
                          }`}>{challenge.score}</span></span>
                          <span className="text-xs text-slate-500 capitalize">{challenge.performanceTier}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate(`/case-studies/${challenge.id}/results/${challenge.attemptId}`)}
                      variant="outline"
                      className="text-xs px-4 py-2 rounded-lg font-semibold border-slate-300 hover:bg-slate-50"
                    >
                      VIEW RESULTS
                    </Button>
                  </div>
                </div>
              )) : (
                <div className="bg-white rounded-xl p-8 shadow-sm border border-[#E8EEF3] text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No completed case studies yet.</p>
                  <Button
                    onClick={() => navigate('/case-studies')}
                    className="mt-4 bg-[#FF9E00] hover:bg-[#E88D00] text-white rounded-full px-6"
                  >
                    Start a Case Study
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Need a Refresher Section */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-[#1E3A5C]">Need a Refresher?</h2>
              <p className="text-slate-600 text-sm">Quick reference materials for clinical topics.</p>
            </div>
            <button 
              onClick={() => navigate('/reference-sheets')}
              className="text-sm text-[#1E3A5C] font-medium flex items-center gap-1 hover:text-[#FF9E00] transition-colors"
            >
              Browse Reference Sheets <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {topics.slice(0, 4).map((topic, index) => {
              const topicName = topic.topic_display_name || topic.name || topic.topic_key;
              return (
                <div
                  key={topic.topic_key || topic.topic_id}
                  className="bg-white rounded-xl p-4 shadow-sm border border-[#E8EEF3] hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      index === 0 ? 'bg-red-100' : index === 1 ? 'bg-blue-100' : index === 2 ? 'bg-green-100' : 'bg-purple-100'
                    }`}>
                      {getTopicIcon(index)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1E3A5C] text-sm">{topicName}</h4>
                      <p className="text-xs text-slate-500">Reference Sheet</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/reference-sheets`)}
                    className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white text-xs py-2 rounded-lg font-medium"
                  >
                    View Reference <ChevronRight className="w-3 h-3 ml-1 inline" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
